// AiTraining.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useClinic } from "../../context/ClinicContext";
import "../../styles/AiTraining.css";

export default function AiTraining() {
  const navigate = useNavigate();
  const { clinicId, setClinicId } = useClinic();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [intakeLink, setIntakeLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ Set clinicId context from token (if not already set)
  useEffect(() => {
    if (token) {
      setClinicId(token);
    }
  }, [token, setClinicId]);

  // 🔙 Go back to customize-chatbot with token in URL
  const handleBack = () => {
    if (token) {
      navigate(`/admin/customize-chatbot?token=${token}`);
    } else {
      navigate("/admin/customize-chatbot");
    }
  };

  const handleSendEmail = async () => {
    if (!clinicId || !intakeLink) {
      setError("Missing clinicId or intake link");
      return;
    }
  
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/send-intake-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clinic_id: clinicId,
          intake_url: intakeLink,
        }),
      });
  
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to send email");
  
      alert("📧 Email sent successfully!");
    } catch (err) {
      setError(err.message || "Failed to send email");
    }
  };
  

  // 🚀 Proceed to publish page with token
  const handlePublish = () => {
    if (token) {
      navigate(`/admin/publish?token=${token}`);
    } else {
      navigate("/admin/publish");
    }
  };

  // 🔗 Generate secure intake link
  const handleGenerateToken = async () => {
    if (!clinicId) {
      setError("Missing clinicId");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/generate-intake-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ clinic_id: clinicId }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to generate token");

      // ✅ Updated: Expect full intake_url from backend
      setIntakeLink(data.intake_url);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (intakeLink) navigator.clipboard.writeText(intakeLink);
  };

  return (
    <div className="container">
      {/* === Header === */}
      <div className="header-row">
        <div className="header-left">
          <img src="/logo.png" alt="Olumis AI Logo" className="logo" />
          <button className="all-clinics-btn" onClick={() => navigate("/admin/all-clinics")}>
            All Clinics
          </button>
        </div>
      </div>

      {/* === Stepper === */}
      <div className="stepper">
        <button className="step active">Create New Clinic</button>
        <div className="step-divider" />
        <button className="step active">Customize Chatbot</button>
        <div className="step-divider" />
        <button className="step current">AI Training</button>
        <div className="step-divider" />
        <button className="step inactive">Publish</button>
      </div>

      {/* === Main Content === */}
      <div className="training-section">
        <div className="intake-controls">
          <button className="generate-btn" onClick={handleGenerateToken} disabled={loading}>
            {loading ? "Generating..." : "Generate Intake Link"}
          </button>

          {clinicId && intakeLink && (
          <div className="intake-info">
            <label>Intake Link:</label>
            <input value={intakeLink} readOnly />
            <button className="copy-btn" onClick={handleCopy}>Copy</button>
            <button className="send-btn" onClick={handleSendEmail}>Send Email</button>
          </div>
        )}


          <div className="status-row">
            <p>Token Status: ✅ Active ❌ Expired</p>
            <button className="regen-btn" onClick={handleGenerateToken}>Regenerate Intake Link</button>
          </div>

          <p>Form Status: ❌ Not Submitted ✅ Complete</p>

          <div className="actions-row">
            <button className="action-btn" onClick={handleCopy}>Copy Intake Link</button>
            <button className="action-btn">View Data</button>
          </div>

          {error && <p style={{ color: "red" }}>{error}</p>}
        </div>

        <button className="start-training-btn">Start Training</button>

        {/* === Chat Preview === */}
        <div className="chat-preview">
          <div className="chatbox">
            <div className="chatbox-controls">
              <button>⛶</button>
              <button>⬇️</button>
              <button>❌</button>
            </div>
            <div className="chat-message">
              <div className="chat-icon"></div>
              <div className="bubble">
                Hello, this is Lisa. I am a professional aesthetic assistant. Please feel free to ask me anything!
              </div>
            </div>
            <div className="chat-input">
              <input type="text" placeholder="Type a message here" />
              <button>➤</button>
            </div>
            <p className="footer">Powered by Olumis AI</p>
          </div>
        </div>

        {/* === Navigation Buttons === */}
        <div className="nav-buttons">
          <button className="nav-btn" onClick={handleBack}>Back</button>
          <button className="nav-btn">Finish Testing</button>
          <button className="nav-btn" onClick={handlePublish}>Publish</button>
        </div>
      </div>
    </div>
  );
}
